import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api, type SettingsResponse } from "../../lib/api";

type FormState = {
  display_name: string;
  height_cm: string;
  sex: string;
  date_of_birth: string;
  timezone: string;
  weight_unit: string;
};

const emptyForm: FormState = {
  display_name: "",
  height_cm: "",
  sex: "",
  date_of_birth: "",
  timezone: "Australia/Melbourne",
  weight_unit: "kg",
};

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

export default function SettingsProfile() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");

  useEffect(() => {
    api
      .settings()
      .then((s) => {
        setForm({
          display_name: s.display_name ?? "",
          height_cm: s.height_cm != null ? String(s.height_cm) : "",
          sex: s.sex ?? "",
          date_of_birth: s.date_of_birth ?? "",
          timezone: s.timezone || "Australia/Melbourne",
          weight_unit: s.weight_unit || "kg",
        });
      })
      .catch(() => setForm(emptyForm));
  }, []);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setStatus("idle");
  }

  function save() {
    setBusy(true);
    setStatus("idle");
    const payload: Partial<SettingsResponse> = {
      display_name: form.display_name,
      height_cm: form.height_cm.trim() === "" ? null : Number(form.height_cm),
      sex: form.sex.trim() === "" ? null : form.sex,
      date_of_birth:
        form.date_of_birth.trim() === "" ? null : form.date_of_birth,
      timezone: form.timezone,
      weight_unit: form.weight_unit,
    };
    api
      .postSettings(payload)
      .then(() => setStatus("saved"))
      .catch(() => setStatus("error"))
      .finally(() => setBusy(false));
  }

  return (
    <div>
      <h2 className="section-title mb-4">Profile</h2>

      <div className="card-pad space-y-4">
        <Field label="Display name">
          <input
            type="text"
            className="input"
            value={form.display_name}
            onChange={(e) => update("display_name", e.target.value)}
          />
        </Field>

        <Field label="Height (cm)">
          <input
            type="number"
            className="input font-display tabular-nums"
            value={form.height_cm}
            onChange={(e) => update("height_cm", e.target.value)}
          />
        </Field>

        <Field label="Sex">
          <select
            className="input"
            value={form.sex}
            onChange={(e) => update("sex", e.target.value)}
          >
            <option value="">Not set</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </Field>

        <Field label="Date of birth">
          <input
            type="date"
            className="input"
            value={form.date_of_birth}
            onChange={(e) => update("date_of_birth", e.target.value)}
          />
        </Field>

        <Field label="Timezone">
          <input
            type="text"
            className="input"
            value={form.timezone}
            onChange={(e) => update("timezone", e.target.value)}
          />
        </Field>

        <Field label="Weight unit">
          <select
            className="input"
            value={form.weight_unit}
            onChange={(e) => update("weight_unit", e.target.value)}
          >
            <option value="kg">kg</option>
            <option value="lb">lb</option>
          </select>
        </Field>

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-accent"
            disabled={busy}
            onClick={save}
          >
            {busy ? "Saving…" : "Save"}
          </button>
          {status === "saved" && (
            <span className="text-income">Saved</span>
          )}
          {status === "error" && (
            <span className="text-loss">Could not save. Try again.</span>
          )}
        </div>
      </div>
    </div>
  );
}
